// v3.3.759 — تحليل التسويق الداخلي
// نظرة دليلك الشاملة (مصاريف حملات + حسابات إلكترونية + رواتب المسوقين مقابل إجمالي الدخل)
// + جدول أداء المسوقين + صفحة أداء لكل مسوق (كشف حجوزاته وتاريخه الشهري)
import React, { useState, useEffect, useCallback } from "react";
import { marketingAnalytics as maApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { FiTrendingUp, FiPlus, FiTrash2, FiX, FiEdit2 } from "react-icons/fi";

const fmt = (n) => (Number(n) || 0).toLocaleString("en-US");
const CATS = {
  campaigns: { label: "حملات تسويقية", icon: "📣", color: "#7c3aed" },
  digital_staff: { label: "موظفو الحسابات الإلكترونية", icon: "💻", color: "#2563eb" },
  other: { label: "أخرى", icon: "📦", color: "#64748b" },
};

const cardS = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px" };
const thS = { padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#64748b", background: "#f8fafc", borderBottom: "2px solid #e2e8f0", textAlign: "right", whiteSpace: "nowrap" };
const tdS = { padding: "8px 12px", fontSize: 12.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" };

export default function MarketingAnalyticsPage() {
  const { user } = useAuth();
  const canManage = (user?.role === "admin" && !user?.impersonating) || user?.can_view_marketing_analytics;

  const yr = new Date().getFullYear();
  const [from, setFrom] = useState(`${yr}-01-01`);
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marketerView, setMarketerView] = useState(null); // {id} → تفاصيل
  const [marketerData, setMarketerData] = useState(null);
  const [showAddExpense, setShowAddExpense] = useState(false);
  // v3.3.763 — فلترة جدول الأداء: بحث + نوع المسوق (داخلي/خارجي)
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  // v3.3.767 — تجميع رسمات المسوق: شهري / ربعي / سنوي
  const [granularity, setGranularity] = useState("monthly");
  const periodize = (monthly) => {
    if (granularity === "monthly") return monthly || [];
    const map = {};
    for (const m of (monthly || [])) {
      const [y, mo] = String(m.month).split("-");
      const key = granularity === "yearly" ? y : `${y}-Q${Math.ceil(Number(mo) / 3)}`;
      if (!map[key]) map[key] = { month: key, count: 0, sales: 0, dalala: 0, share: 0 };
      map[key].count += m.count; map[key].sales += m.sales;
      map[key].dalala += m.dalala; map[key].share += m.share;
    }
    return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
  };
  const granLabel = granularity === "monthly" ? "الشهري" : granularity === "quarterly" ? "الربعي" : "السنوي";

  // v3.3.764 — فلتر حالة الحجوزات في كشف المسوق (متعدد الاختيار)
  const ALL_STATUSES = ["done", "in_progress", "cancelled"];
  const [resFilter, setResFilter] = useState(ALL_STATUSES);
  const toggleResFilter = (k) => setResFilter(prev =>
    prev.includes(k) ? (prev.length > 1 ? prev.filter(x => x !== k) : prev) : [...prev, k]);
  const [newExp, setNewExp] = useState({ month: new Date().toISOString().slice(0, 7), category: "campaigns", label: "", amount: "", notes: "" });
  const [salaryEdit, setSalaryEdit] = useState(null); // {id, value}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ex] = await Promise.all([
        maApi.overview({ from, to }),
        maApi.listExpenses({ from, to }),
      ]);
      setData(ov.data);
      setExpenses(ex.data.expenses || []);
    } catch (e) { toast.error(e.response?.data?.error || "فشل تحميل التحليل"); }
    setLoading(false);
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!marketerView) { setMarketerData(null); return; }
    maApi.marketer(marketerView.id, { from, to })
      .then(r => setMarketerData(r.data))
      .catch(() => toast.error("فشل تحميل أداء المسوق"));
  }, [marketerView, from, to]);

  const addExpense = async () => {
    if (!newExp.month || !newExp.amount) return toast.error("الشهر والمبلغ مطلوبان");
    try {
      await maApi.addExpense(newExp);
      toast.success("تمت إضافة المصروف");
      setShowAddExpense(false);
      setNewExp({ month: new Date().toISOString().slice(0, 7), category: "campaigns", label: "", amount: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.error || "فشل الإضافة"); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("حذف هذا المصروف؟")) return;
    try { await maApi.deleteExpense(id); toast.success("تم الحذف"); load(); }
    catch { toast.error("فشل الحذف"); }
  };

  const saveSalary = async (id) => {
    try {
      await maApi.setSalary(id, salaryEdit.value);
      toast.success("تم تحديث الراتب");
      setSalaryEdit(null); load();
    } catch { toast.error("فشل تحديث الراتب"); }
  };

  const ov = data?.overview;
  const inputS = { padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontFamily: "inherit", fontSize: 12, boxSizing: "border-box" };

  return (
    <div dir="rtl" style={{ padding: 20 }}>
      {/* Header + فلتر الفترة */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <FiTrendingUp /> تحليل التسويق الداخلي
          </h1>
          <p style={{ fontSize: 12, color: "#64748b", margin: "3px 0 0" }}>أداء المسوقين + مصاريف دليلك التسويقية مقابل الدخل</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>من</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputS} />
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>إلى</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputS} />
        </div>
      </div>

      {loading ? <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div> : ov && (
        <>
          {/* ══ نظرة دليلك الشاملة ══ */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 18 }}>
            {[
              { l: "📣 مصاريف الحملات", v: ov.campaigns, c: "#7c3aed" },
              { l: "💻 موظفو الحسابات الإلكترونية", v: ov.digital_staff, c: "#2563eb" },
              { l: "👥 رواتب المسوقين (الفترة)", v: ov.salaries_total, c: "#d97706" },
              { l: "📦 مصاريف أخرى", v: ov.other, c: "#64748b" },
              { l: "💸 إجمالي المصاريف", v: ov.total_expenses, c: "#dc2626" },
              { l: "💎 إجمالي الدخل (الدلالات)", v: ov.total_income, c: "#166534" },
              { l: "⚖️ الصافي", v: ov.net, c: ov.net >= 0 ? "#166534" : "#dc2626" },
            ].map(x => (
              <div key={x.l} style={{ ...cardS, borderTop: `3px solid ${x.c}` }}>
                <div style={{ fontSize: 10.5, color: "#64748b", fontWeight: 700, marginBottom: 6 }}>{x.l}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: x.c }}>{fmt(x.v)} <span style={{ fontSize: 10 }}>ر.س</span></div>
              </div>
            ))}
          </div>

          {/* ══ جدول أداء المسوقين ══ */}
          {(() => {
            // v3.3.763 — تطبيق الفلترة (بحث + نوع)
            const shownPerf = (data.performance || []).filter(p =>
              (typeFilter === "all" || p.mtype === typeFilter) &&
              (!searchQ.trim() || (p.name || "").includes(searchQ.trim()) || (p.company || "").includes(searchQ.trim()) || (p.phone || "").includes(searchQ.trim())));
            return (
          <div style={{ ...cardS, padding: 0, overflow: "hidden", marginBottom: 18 }}>
            <div style={{ padding: "10px 16px", background: "#f0fdfa", borderBottom: "1px solid #99f6e4", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#0f766e" }}>👥 أداء المسوقين ({shownPerf.length}{shownPerf.length !== data.performance.length ? ` من ${data.performance.length}` : ""})</span>
              {/* v3.3.763 — فلترة: نوع المسوق + بحث */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {[["all", "الكل"], ["internal", "🏠 داخلي"], ["external", "🌐 خارجي"]].map(([k, l]) => (
                  <button key={k} onClick={() => setTypeFilter(k)}
                    style={{ padding: "4px 12px", borderRadius: 14, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                      background: typeFilter === k ? "#0f766e" : "#fff", color: typeFilter === k ? "#fff" : "#475569",
                      border: `1px solid ${typeFilter === k ? "#0f766e" : "#e2e8f0"}` }}>
                    {l}
                  </button>
                ))}
                <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="🔍 بحث باسم المسوق..."
                  style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontFamily: "inherit", fontSize: 11.5, width: 180 }} />
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={thS}>المسوق</th>
                  <th style={thS}>الراتب الشهري</th>
                  <th style={thS}>تكلفة الراتب (الفترة)</th>
                  <th style={thS}>البيعات (تمت)</th>
                  <th style={thS}>إجمالي المبيعات</th>
                  <th style={thS}>إجمالي الدلالة</th>
                  <th style={thS}>حصة المسوق</th>
                  <th style={thS}>الصافي للشركة</th>
                </tr></thead>
                <tbody>
                  {shownPerf.map(p => (
                    <tr key={p.marketer_id} style={{ cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ ...tdS, fontWeight: 700 }} onClick={() => setMarketerView({ id: p.marketer_id })}>
                        {p.name}
                        {/* v3.3.763 — شارة نوع المسوق */}
                        {p.mtype && (
                          <span style={{ marginRight: 6, fontSize: 9, fontWeight: 700, borderRadius: 10, padding: "1px 8px",
                            background: p.mtype === "internal" ? "#dcfce7" : "#dbeafe",
                            color: p.mtype === "internal" ? "#166534" : "#1e40af" }}>
                            {p.mtype === "internal" ? "🏠 داخلي" : "🌐 خارجي"}
                          </span>
                        )}
                        {p.company && <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>{p.company}</div>}
                      </td>
                      <td style={tdS}>
                        {salaryEdit?.id === p.marketer_id ? (
                          <span style={{ display: "inline-flex", gap: 4 }}>
                            <input type="number" autoFocus value={salaryEdit.value}
                              onChange={e => setSalaryEdit({ ...salaryEdit, value: e.target.value })}
                              onKeyDown={e => { if (e.key === "Enter") saveSalary(p.marketer_id); if (e.key === "Escape") setSalaryEdit(null); }}
                              style={{ ...inputS, width: 90, padding: "4px 6px" }} />
                            <button onClick={() => saveSalary(p.marketer_id)} style={{ background: "#166534", color: "#fff", border: "none", borderRadius: 5, padding: "2px 8px", cursor: "pointer", fontSize: 10 }}>حفظ</button>
                            <button onClick={() => setSalaryEdit(null)} style={{ background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 5, padding: "2px 6px", cursor: "pointer", fontSize: 10 }}>✕</button>
                          </span>
                        ) : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {p.monthly_salary ? fmt(p.monthly_salary) : <span style={{ color: "#cbd5e1" }}>—</span>}
                            {canManage && <FiEdit2 size={11} style={{ cursor: "pointer", color: "#94a3b8" }}
                              onClick={() => setSalaryEdit({ id: p.marketer_id, value: p.monthly_salary || "" })} />}
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdS, color: "#d97706", fontWeight: 600 }} onClick={() => setMarketerView({ id: p.marketer_id })}>{fmt(p.salary_cost)}</td>
                      <td style={tdS} onClick={() => setMarketerView({ id: p.marketer_id })}>
                        {/* v3.3.766 — البيعات = التي تمت؛ والإجمالي الفعّال بجانبها */}
                        <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{p.sales_count ?? 0}</span>
                        <span style={{ fontSize: 9.5, color: "#94a3b8", marginRight: 5 }}>من {p.reservations_count} حجز</span>
                      </td>
                      <td style={{ ...tdS, fontWeight: 700, color: "#7c3aed" }} onClick={() => setMarketerView({ id: p.marketer_id })}>{fmt(p.total_sales)}</td>
                      <td style={{ ...tdS, fontWeight: 700, color: "#166534" }} onClick={() => setMarketerView({ id: p.marketer_id })}>{fmt(p.total_dalala)}</td>
                      <td style={{ ...tdS, fontWeight: 600, color: "#2563eb" }} onClick={() => setMarketerView({ id: p.marketer_id })}>{fmt(p.marketer_share)}</td>
                      <td style={{ ...tdS, fontWeight: 800, color: p.net_for_company >= 0 ? "#166534" : "#dc2626" }} onClick={() => setMarketerView({ id: p.marketer_id })}>{fmt(p.net_for_company)}</td>
                    </tr>
                  ))}
                  {shownPerf.length === 0 && (
                    <tr><td colSpan={8} style={{ ...tdS, textAlign: "center", color: "#94a3b8", padding: 30 }}>لا توجد بيانات مطابقة للفلترة</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
            );
          })()}

          {/* ══ المصاريف التسويقية الشهرية ══ */}
          <div style={{ ...cardS, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "10px 16px", background: "#faf5ff", borderBottom: "1px solid #e9d5ff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#7c3aed" }}>🧾 المصاريف التسويقية الشهرية ({expenses.length})</span>
              {canManage && (
                <button onClick={() => setShowAddExpense(true)}
                  style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 7, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
                  <FiPlus size={13} /> إضافة مصروف
                </button>
              )}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={thS}>الشهر</th><th style={thS}>التصنيف</th><th style={thS}>البيان</th>
                  <th style={thS}>المبلغ</th><th style={thS}>ملاحظات</th><th style={thS}></th>
                </tr></thead>
                <tbody>
                  {expenses.map(e => {
                    const cat = CATS[e.category] || CATS.other;
                    return (
                      <tr key={e.id}>
                        <td style={{ ...tdS, fontWeight: 600, whiteSpace: "nowrap" }}>{String(e.month).slice(0, 7)}</td>
                        <td style={tdS}>
                          <span style={{ background: `${cat.color}18`, color: cat.color, padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                            {cat.icon} {cat.label}
                          </span>
                        </td>
                        <td style={tdS}>{e.label || "—"}</td>
                        <td style={{ ...tdS, fontWeight: 700, color: "#dc2626", whiteSpace: "nowrap" }}>{fmt(e.amount)} ر.س</td>
                        <td style={{ ...tdS, fontSize: 11, color: "#64748b" }}>{e.notes || "—"}</td>
                        <td style={tdS}>
                          {canManage && <FiTrash2 size={13} style={{ cursor: "pointer", color: "#dc2626" }} onClick={() => deleteExpense(e.id)} />}
                        </td>
                      </tr>
                    );
                  })}
                  {expenses.length === 0 && (
                    <tr><td colSpan={6} style={{ ...tdS, textAlign: "center", color: "#94a3b8", padding: 30 }}>لا توجد مصاريف مسجلة في الفترة</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══ بوب أب إضافة مصروف ══ */}
      {showAddExpense && (
        <div onClick={() => setShowAddExpense(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div dir="rtl" onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 14, padding: 20, width: "min(480px, 100%)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 15, fontWeight: 800 }}>🧾 إضافة مصروف تسويقي</span>
              <button onClick={() => setShowAddExpense(false)} style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer" }}><FiX /></button>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>الشهر *</label>
                  <input type="month" value={newExp.month} onChange={e => setNewExp({ ...newExp, month: e.target.value })} style={{ ...inputS, width: "100%" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>التصنيف *</label>
                  <select value={newExp.category} onChange={e => setNewExp({ ...newExp, category: e.target.value })} style={{ ...inputS, width: "100%" }}>
                    {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>البيان</label>
                <input value={newExp.label} onChange={e => setNewExp({ ...newExp, label: e.target.value })} placeholder="مثال: حملة سناب شات يوليو" style={{ ...inputS, width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>المبلغ (ر.س) *</label>
                <input type="number" value={newExp.amount} onChange={e => setNewExp({ ...newExp, amount: e.target.value })} style={{ ...inputS, width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>ملاحظات</label>
                <textarea value={newExp.notes} onChange={e => setNewExp({ ...newExp, notes: e.target.value })} rows={2} style={{ ...inputS, width: "100%", resize: "vertical" }} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={addExpense} style={{ flex: 1, padding: "10px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>إضافة</button>
                <button onClick={() => setShowAddExpense(false)} style={{ padding: "10px 18px", background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ بوب أب أداء مسوق ══ */}
      {marketerView && (
        <div onClick={() => setMarketerView(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 9000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "4vh 16px", overflowY: "auto" }}>
          <div dir="rtl" onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 14, width: "min(900px, 100%)", boxShadow: "0 20px 60px rgba(0,0,0,0.35)", overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", background: "linear-gradient(135deg,#0f766e,#14b8a6)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>📈 أداء المسوق: {marketerData?.marketer?.name || "..."}</div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11, marginTop: 2 }}>
                  الفترة {from} ← {to}
                  {marketerData?.marketer?.monthly_salary ? ` · الراتب الشهري ${fmt(marketerData.marketer.monthly_salary)} ر.س` : ""}
                </div>
              </div>
              <button onClick={() => setMarketerView(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 8, width: 30, height: 30, cursor: "pointer", fontSize: 15 }}>✕</button>
            </div>
            {!marketerData ? (
              <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
            ) : (
              <div style={{ padding: 16 }}>
                {/* إجماليات — v3.3.765: تفصيل الحجوزات (إجمالي/تمت/تحت الإجراء/لم تتم) */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: 14 }}>
                  {(() => {
                    const all = marketerData.reservations || [];
                    const cnt = (k) => all.filter(r => r.status_class === k).length;
                    return [
                      { l: "إجمالي الحجوزات", v: all.length, c: "#1e40af" },
                      { l: "✅ تمت", v: cnt("done"), c: "#166534" },
                      { l: "⏳ تحت الإجراء", v: cnt("in_progress"), c: "#d97706" },
                      { l: "❌ لم تتم", v: cnt("cancelled"), c: "#dc2626" },
                      { l: "إجمالي المبيعات", v: fmt(marketerData.totals.sales), c: "#7c3aed" },
                      { l: "إجمالي الدلالة", v: fmt(marketerData.totals.dalala), c: "#166534" },
                      { l: "حصة المسوق", v: fmt(marketerData.totals.share), c: "#2563eb" },
                    ];
                  })().map(x => (
                    <div key={x.l} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>{x.l}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: x.c, marginTop: 3 }}>{x.v}</div>
                    </div>
                  ))}
                </div>

                {/* v3.3.767 — فلتر تجميع الرسمات: شهري/ربعي/سنوي */}
                {marketerData.monthly.length > 0 && (
                  <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                    {[["monthly", "شهري"], ["quarterly", "ربعي"], ["yearly", "سنوي"]].map(([k, l]) => (
                      <button key={k} onClick={() => setGranularity(k)}
                        style={{ padding: "5px 16px", borderRadius: 14, fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                          background: granularity === k ? "#0f766e" : "#fff", color: granularity === k ? "#fff" : "#475569",
                          border: `1px solid ${granularity === k ? "#0f766e" : "#e2e8f0"}` }}>
                        {l}
                      </button>
                    ))}
                  </div>
                )}

                {/* الأداء الزمني (الدلالة) — v3.3.767: يتجمع حسب الفلتر */}
                {marketerData.monthly.length > 0 && (
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#0f766e", marginBottom: 10 }}>📊 الأداء {granLabel} (الدلالة)</div>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 90, overflowX: "auto", paddingBottom: 4 }}>
                      {(() => {
                        const periods = periodize(marketerData.monthly);
                        const max = Math.max(...periods.map(m => m.dalala), 1);
                        return periods.map(m => (
                          <div key={m.month} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 46 }}>
                            <span style={{ fontSize: 9, fontWeight: 700, color: "#166534" }}>{fmt(m.dalala)}</span>
                            <div title={`${m.month}: ${m.count} حجز · دلالة ${fmt(m.dalala)}`}
                              style={{ width: 26, height: Math.max(4, Math.round((m.dalala / max) * 55)), background: "linear-gradient(180deg,#14b8a6,#0f766e)", borderRadius: "4px 4px 0 0" }} />
                            <span style={{ fontSize: 8.5, color: "#64748b", whiteSpace: "nowrap" }}>{m.month}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* v3.3.764 — الأداء الزمني (عدد الحجوزات) — v3.3.767: يتجمع حسب الفلتر */}
                {marketerData.monthly.length > 0 && (
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#1e40af", marginBottom: 10 }}>📅 الأداء {granLabel} (عدد الحجوزات)</div>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80, overflowX: "auto", paddingBottom: 4 }}>
                      {(() => {
                        const periods = periodize(marketerData.monthly);
                        const max = Math.max(...periods.map(m => m.count), 1);
                        return periods.map(m => (
                          <div key={m.month} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 46 }}>
                            <span style={{ fontSize: 9.5, fontWeight: 700, color: "#1e40af" }}>{m.count}</span>
                            <div title={`${m.month}: ${m.count} حجز`}
                              style={{ width: 26, height: Math.max(4, Math.round((m.count / max) * 48)), background: "linear-gradient(180deg,#60a5fa,#1d4ed8)", borderRadius: "4px 4px 0 0" }} />
                            <span style={{ fontSize: 8.5, color: "#64748b", whiteSpace: "nowrap" }}>{m.month}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* v3.3.764 — توزيع حجوزاته على مشاريع الشركة */}
                {(marketerData.by_project || []).length > 0 && (
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#7c3aed", marginBottom: 10 }}>🏢 حجوزاته حسب المشروع</div>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 14, height: 100, overflowX: "auto", paddingBottom: 4 }}>
                      {(() => {
                        const max = Math.max(...marketerData.by_project.map(p => p.count), 1);
                        return marketerData.by_project.map(p => (
                          <div key={p.project} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 70 }}>
                            <span style={{ fontSize: 10, fontWeight: 800, color: "#7c3aed" }}>{p.count} حجز</span>
                            <div title={`${p.project}: ${p.count} حجز · دلالة ${fmt(p.dalala)}`}
                              style={{ width: 34, height: Math.max(5, Math.round((p.count / max) * 58)), background: "linear-gradient(180deg,#a78bfa,#6d28d9)", borderRadius: "5px 5px 0 0" }} />
                            <span style={{ fontSize: 9, color: "#475569", fontWeight: 600, whiteSpace: "nowrap", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis" }}>{p.project}</span>
                            <span style={{ fontSize: 8.5, color: "#94a3b8" }}>{fmt(p.dalala)} دلالة</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* كشف الحجوزات — v3.3.764: يشمل التي لم تتم + فلتر حالة متعدد */}
                {(() => {
                  const STATUS_META = {
                    done: { l: "تمت", bg: "#dcfce7", c: "#166534" },
                    in_progress: { l: "تحت الإجراء", bg: "#fef3c7", c: "#92400e" },
                    cancelled: { l: "لم تتم", bg: "#fee2e2", c: "#991b1b" },
                  };
                  const shownRes = marketerData.reservations.filter(r => resFilter.includes(r.status_class));
                  const allSelected = resFilter.length === ALL_STATUSES.length;
                  return (
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ padding: "8px 14px", background: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: "#1e293b" }}>
                      🗂️ كشف الحجوزات ({shownRes.length}{shownRes.length !== marketerData.reservations.length ? ` من ${marketerData.reservations.length}` : ""})
                    </span>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      <button onClick={() => setResFilter(ALL_STATUSES)}
                        style={{ padding: "3px 12px", borderRadius: 12, fontSize: 10.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                          background: allSelected ? "#1e293b" : "#fff", color: allSelected ? "#fff" : "#475569",
                          border: `1px solid ${allSelected ? "#1e293b" : "#e2e8f0"}` }}>
                        الكل
                      </button>
                      {ALL_STATUSES.map(k => {
                        const on = resFilter.includes(k);
                        const meta = STATUS_META[k];
                        const cnt = marketerData.reservations.filter(r => r.status_class === k).length;
                        return (
                          <button key={k} onClick={() => toggleResFilter(k)}
                            style={{ padding: "3px 12px", borderRadius: 12, fontSize: 10.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                              background: on ? meta.bg : "#fff", color: on ? meta.c : "#94a3b8",
                              border: `1px solid ${on ? meta.c : "#e2e8f0"}`, opacity: on ? 1 : 0.75 }}>
                            {meta.l} ({cnt})
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr>
                        <th style={thS}>#</th><th style={thS}>التاريخ</th><th style={thS}>العميل</th><th style={thS}>المشروع</th>
                        <th style={thS}>القيمة</th><th style={thS}>الدلالة</th><th style={thS}>حصته</th><th style={thS}>الحالة</th>
                      </tr></thead>
                      <tbody>
                        {shownRes.map(r => (
                          <tr key={r.id}>
                            <td style={tdS}>
                              {/* v3.3.763 — الباراميتر الصحيح لفتح الحجز نفسه هو ?reservation */}
                              <span onClick={() => window.open(`/?reservation=${r.id}`, "_blank")}
                                style={{ color: "#2563eb", fontWeight: 700, cursor: "pointer", background: "#eff6ff", padding: "2px 8px", borderRadius: 6, fontSize: 11 }}>
                                #{r.id} ↗
                              </span>
                            </td>
                            <td style={{ ...tdS, whiteSpace: "nowrap", color: "#64748b", fontSize: 11 }}>{String(r.date || "").slice(0, 10)}</td>
                            <td style={{ ...tdS, fontWeight: 600 }}>{r.client_name || "—"}</td>
                            <td style={{ ...tdS, fontSize: 11, color: "#7c3aed", fontWeight: 600, whiteSpace: "nowrap" }}>{r.project_name || "—"}</td>
                            <td style={{ ...tdS, fontWeight: 700, color: "#7c3aed", whiteSpace: "nowrap" }}>{fmt(r.value)}</td>
                            <td style={{ ...tdS, fontWeight: 700, color: "#166534", whiteSpace: "nowrap" }}>{fmt(r.dalala_amount)}</td>
                            <td style={{ ...tdS, fontWeight: 600, color: "#2563eb", whiteSpace: "nowrap" }}>{fmt(r.marketer_share)}</td>
                            <td style={tdS}>
                              {/* v3.3.764 — لون ثالث أحمر للتي لم تتم */}
                              <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 10, padding: "1px 8px", whiteSpace: "nowrap",
                                background: STATUS_META[r.status_class].bg,
                                color: STATUS_META[r.status_class].c }}>
                                {r.operation_status || "—"}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {shownRes.length === 0 && (
                          <tr><td colSpan={8} style={{ ...tdS, textAlign: "center", color: "#94a3b8", padding: 24 }}>لا توجد حجوزات مطابقة للفلتر</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
